import { format, startOfDay } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { RichInputContextProvider, RichMediaInput, RichMediaState, useRichInput } from '@bonusly/common-action-input';
import { useFetchRecipients } from '@bonusly/common-give-box';
import { RichTextInput, useRichText } from '@bonusly/common-rich-text-editor';
import {
	Box,
	Button,
	Checkbox,
	CheckboxGroup,
	Flex,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	rem,
	Spacer,
	Stack,
	Text,
	useToast,
} from '@bonusly/design-system';
import { GroupType, Recipient } from '@bonusly/recipient-entities';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { useHomepageRedesignEnabled } from '../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { FormLabel } from '../../form/form-label';
import { useDeleteAnnouncement, useInvalidateAnnouncementsQueries, useUpdateAnnouncement } from '../hooks/use-announcements';
import { useGetEveryoneRecipient } from '../hooks/use-recipients';
import { dateIsFutureOrTodayOrSame, endDateIsAfterStartDate, isFutureDate } from '../lib/form-validations';
import { Announcement, FormValues } from '../types/announcements';
import { createRecipientContent } from '../utils/content';
import { formatDateISOString } from '../utils/date';
import { ImageUploadField } from './image-upload-field';
import { LoadingAnnouncement } from './loading-announcement';
import SelectIncentivesDropdown from './select-incentives-dropdown';

export interface EditAnnouncementModalProps {
	announcement: Announcement;
	isOpen: boolean;
	onClose: VoidFunction;
}

const EditAnnouncementModal: FC<EditAnnouncementModalProps> = ({ announcement, isOpen, onClose }) => {
	const { t } = useTranslation('announcements');
	const { track } = useAnalytics();
	const toast = useToast();
	const invalidateQueries = useInvalidateAnnouncementsQueries();
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();
	const fetchRecipients = useFetchRecipients({
		search_users: false,
		include_self: true,
	});
	const company = useCurrentCompany();
	const setInitialRecipients = (everyoneRecipients: Recipient[]) => {
		if (announcement.reason_tokens.length > 0) {
			const announcementRecipients = announcement.reason_tokens.map((token) => {
				return {
					dimension: token.type,
					displayName: token.display_name,
					id: token.id,
					type: 'group',
					userIds: token.user_ids,
					username: token.token.replace('@', ''),
				} as Recipient;
			});
			recipientEditor?.commands.setContent(createRecipientContent(announcementRecipients));
		} else {
			recipientEditor?.commands.setContent(createRecipientContent(everyoneRecipients));
		}
	};
	const { data: everyoneRecipients, isLoading: isLoadingRecipient } = useGetEveryoneRecipient({
		onSuccess: (recipients) => {
			setInitialRecipients(recipients);
		},
	});
	const { mutate: updateAnnouncement, isLoading } = useUpdateAnnouncement();
	const { mutate: deleteAnnouncement, isLoading: isDeleting } = useDeleteAnnouncement();

	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [removeImage, setRemoveImage] = useState<boolean>(false);

	const msteamsMessageEnabled = company.chat_integrations.has_ms_teams_integration;
	const slackMessageEnabled = company.chat_integrations.has_slack_integration;

	const today = format(startOfDay(new Date()), 'yyyy-MM-dd');

	// form setup
	const methods = useForm<FormValues>({
		defaultValues: {
			description: '',
			email_notification: false,
			send_slack_message: false,
			send_ms_teams_message: false,
			title: '',
			recipients: [],
			start_date: today,
		},
		shouldFocusError: false,
	});
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		watch,
	} = methods;

	// NOTE: since description is a rich text editor we need to manually register the field
	register('description', {
		validate: {
			value: () => {
				if (descriptionText.trim() === '') {
					return t('modal.description_required');
				}

				if (descriptionText.length > 250) {
					return t('modal.description_too_long');
				}

				return true;
			},
		},
	});

	// NOTE: since recipients is a rich input we need to manually register the field for error handling
	register('recipients', {
		required: t('modal.recipient_required'),
	});

	// description rich text editor setup
	const {
		editor: descriptionEditor,
		html: descriptionHtml,
		text: descriptionText,
	} = useRichText({
		placeholder: t('modal.description_placeholder'),
		// set description value on load here
		content: announcement.description,
		editorProps: {
			attributes: {
				'data-testid': 'edit-announcement-description',
			},
		},
	});

	// recipient rich input setup
	const { editor: recipientEditor, uniqueId } = useRichInput({
		fetchRecipients,
		modules: ['recipient'],
		options: {
			recipient: {
				matchWithoutTrigger: true,
			},
		},
		placeholder: t('modal.recipient_placeholder'),
	});

	// set form values and clear error on load
	useEffect(() => {
		if (!isOpen) return;

		setValue('title', announcement.title);
		setValue('start_date', announcement.start_date.split('T')[0]);
		setValue('end_date', announcement.end_date.split('T')[0]);
		setValue('claimable_award_id', announcement.claimable_award_id || '');
		setValue('email_notification', announcement.email_notification || false);
		setValue('send_slack_message', announcement.send_slack_message || false);
		setValue('send_ms_teams_message', announcement.send_ms_teams_message || false);
		setValue('image', undefined);
		setValue(
			'recipients',
			announcement.reason_tokens
				? announcement.reason_tokens
						.map((token) => token.id)
						// NOTE: id can be undefined for non-group recipients
						.filter((id): id is string => !!id)
				: []
		);
		setRemoveImage(false);
		setErrorMessage(null);

		if (recipientData.recipients.length === 0) {
			setInitialRecipients(everyoneRecipients);
		}

		track(AnalyticsEvent.Announcements.EditAnnouncementModalViewed);
	}, [announcement, isOpen, setValue, track]);

	const title = watch('title');
	const description = watch('description');
	const recipients = watch('recipients');
	const claimableAwardId = watch('claimable_award_id');

	// NOTE: we need to update the form value and trigger validation when description changes manually
	useEffect(() => {
		if (description !== descriptionHtml) {
			setValue('description', descriptionHtml, {
				shouldValidate: !!description,
			});
		}
	}, [description, descriptionHtml, setValue]);

	// NOTE: we need to update the form value and trigger validation when recipients change manually
	const recipientData = RichMediaState.parse(recipientEditor);
	useEffect(() => {
		const existingRecipientIds = recipients.join(',');
		const newRecipients = recipientData.recipients.map((recipient) => recipient.id);
		//  compare array contents
		if (existingRecipientIds !== newRecipients.join(',')) {
			setValue('recipients', newRecipients, {
				shouldValidate: true,
			});
		}
	}, [recipients, recipientData.recipients, setValue]);

	const onSubmit = (formValues: FormValues) => {
		if (errorMessage) setErrorMessage(null);

		const params = {
			...formValues,
			claimable_award_id: formValues.claimable_award_id || null,
			end_date: formatDateISOString(formValues.end_date),
			id: announcement.id,
			image: formValues.image ? formValues.image[0] : null,
			remove_image: removeImage,
			...(formValues.start_date && {
				start_date: formatDateISOString(formValues.start_date),
			}),
			reason_tokens: recipientData.recipients.map(({ id, dimension, displayName, username, userIds }) => ({
				context: 'recipient' as const,
				display_name: displayName,
				id: id,
				token: `@${username}`,
				type: (dimension as GroupType) || 'username',
				user_ids: userIds,
			})),
		};
		track(AnalyticsEvent.Announcements.UpdateAnnouncementRequestSubmitted);
		updateAnnouncement(params, {
			onError: () => {
				setErrorMessage(t('modal.edit_error_message'));
				track(AnalyticsEvent.Announcements.UpdateAnnouncementFailure);
			},
			onSuccess: () => {
				onClose();
				track(AnalyticsEvent.Announcements.UpdateAnnouncementSuccess);
				invalidateQueries();
				toast({
					title: t('modal.edit_success_message'),
					status: 'success',
					position: 'bottom-right',
				});
			},
		});
	};

	const handleDelete = () => {
		if (errorMessage) setErrorMessage(null);
		deleteAnnouncement(
			{ id: announcement.id },
			{
				onError: () => {
					setErrorMessage(t('modal.delete_error_message'));
				},
				onSuccess: () => {
					onClose();
					invalidateQueries();
					toast({
						title: t('modal.delete_success_message'),
						status: 'success',
						position: 'bottom-right',
					});
				},
			}
		);
	};

	const getButtonText = () => {
		const startDate = watch('start_date');

		if (startDate === today) return t('modal.post_button');
		return t('modal.save_button');
	};

	const isSaveDisabled = () => {
		const formValues = watch();
		const { title, description, image, start_date, email_notification, send_slack_message, send_ms_teams_message, end_date, claimable_award_id, recipients } =
			formValues;
		const announcementClaimableAwardId = announcement.claimable_award_id || '';
		const announcementImageName = announcement.image?.filename;
		const imageUploadName = image?.[0]?.name;
		const neverAddedImage = !imageUploadName && !announcementImageName;
		const existingImageNotRemoved = !!announcementImageName && !imageUploadName && !removeImage;
		const announcementRecipients = announcement.reason_tokens.map((token) => token.id).join(',');

		if (isLoadingRecipient) return true;

		return (
			title === announcement.title &&
			description === announcement.description &&
			start_date === announcement.start_date.split('T')[0] &&
			end_date === announcement.end_date.split('T')[0] &&
			claimable_award_id === announcementClaimableAwardId &&
			email_notification === announcement.email_notification &&
			send_slack_message === announcement.send_slack_message &&
			send_ms_teams_message === announcement.send_ms_teams_message &&
			recipients.join(',') === announcementRecipients &&
			(neverAddedImage || existingImageNotRemoved)
		);
	};

	const isNotificationCheckboxDisabled = () => {
		const startDate = watch('start_date');
		return !isFutureDate(startDate);
	};

	return (
		<>
			<Modal
				autoFocus={false}
				isOpen={isOpen}
				onClose={() => {
					track(AnalyticsEvent.Announcements.EditAnnouncementModalClosed);
					onClose();
				}}
				scrollBehavior="inside"
				size={{ base: 'full', md: 'xl' }}
			>
				<ModalOverlay />
				<FormProvider {...methods}>
					<ModalContent as="form" onSubmit={handleSubmit(onSubmit)} borderRadius={{ base: 0, md: 'xl' }}>
						<ModalHeader
							{...(!isHomepageRedesignEnabled && {
								borderBottomWidth: rem(1),
								borderColor: 'border.soft',
								borderStyle: 'solid',
							})}
							{...(isHomepageRedesignEnabled && {
								px: rem(24),
								pt: rem(16),
								pb: rem(12),
								fontSize: 'xl',
								fontWeight: 'semibold',
								borderBottomWidth: 0,
							})}
							color="text.default"
						>
							{t('modal.edit_header')}
						</ModalHeader>
						<ModalCloseButton data-testid="close-button" size="lg" borderRadius="full" />

						{isLoadingRecipient ? (
							<LoadingAnnouncement />
						) : (
							<ModalBody px={5} pt={isHomepageRedesignEnabled ? 0 : 5} pb={0}>
								<Stack spacing={5}>
									<FormControl isInvalid={!!errors.recipients}>
										<FormLabel fontSize={50}>{t('modal.recipient_label')}</FormLabel>
										<RichInputContextProvider value={{ parentComponent: 'announcement-modal' }}>
											<RichMediaInput
												borderColor={errors?.recipients ? 'red.500' : 'border.input'}
												borderWidth={errors?.recipients ? rem(2) : rem(1)}
												borderRadius="md"
												editor={recipientEditor}
												fontSize={50}
												uniqueId={uniqueId}
											/>
										</RichInputContextProvider>
										<FormErrorMessage>{errors?.recipients?.message}</FormErrorMessage>
										<FormHelperText>{t('modal.recipient_helper')}</FormHelperText>
									</FormControl>
									<FormControl isInvalid={!!errors.title}>
										<FormLabel fontSize={50} _disabled={{ color: 'text.default' }}>
											{t('modal.title_label')}
										</FormLabel>
										<Input
											data-testid="title-input"
											placeholder={t('modal.title_placeholder')}
											maxLength={50}
											borderColor="border.input"
											fontSize={50}
											_hover={{ borderColor: 'border.input' }}
											_focus={{
												borderColor: 'border.input',
												boxShadow: 'none',
											}}
											_disabled={{ color: 'text.default' }}
											{...register('title', {
												required: t('modal.title_required'),
											})}
										/>
										<FormErrorMessage>{errors?.title?.message}</FormErrorMessage>
										<FormHelperText>{t('modal.title_helper', { count: title.length })}</FormHelperText>
									</FormControl>

									<FormControl isInvalid={!!errors.description}>
										<FormLabel fontSize={50}>{t('modal.description_label')}</FormLabel>
										<Box borderColor={errors?.description ? 'red.500' : 'border.input'} borderWidth={errors?.description ? rem(2) : rem(1)} borderRadius="md">
											<RichTextInput editor={descriptionEditor} fontSize={50} border="none" />
										</Box>
										<FormErrorMessage>{errors?.description?.message}</FormErrorMessage>
										<FormHelperText>
											{t('modal.description_helper', {
												count: descriptionText.length,
											})}
										</FormHelperText>
									</FormControl>

									<FormControl isInvalid={!!errors.start_date}>
										<FormLabel fontSize={50}>{t('modal.start_date_label')}</FormLabel>
										<Input
											data-testid="start-date-input"
											type="date"
											borderColor="border.input"
											fontSize={50}
											_hover={{ borderColor: 'border.input' }}
											_focus={{
												borderColor: 'border.input',
												boxShadow: 'none',
											}}
											{...register('start_date', {
												required: t('modal.start_date_required'),
												validate: {
													sameOrAfter: (value: string) => {
														if (dateIsFutureOrTodayOrSame(value, announcement.start_date)) return true;
														return t('modal.start_date_same_or_after');
													},
												},
											})}
										/>
										<FormErrorMessage>{errors?.start_date?.message}</FormErrorMessage>
										<FormHelperText>{t('modal.start_date_helper')}</FormHelperText>
									</FormControl>

									<FormControl isInvalid={!!errors.end_date}>
										<FormLabel fontSize={50}>{t('modal.end_date_label')}</FormLabel>
										<Input
											data-testid="end-date-input"
											type="date"
											borderColor="border.input"
											fontSize={50}
											_hover={{ borderColor: 'border.input' }}
											_focus={{
												borderColor: 'border.input',
												boxShadow: 'none',
											}}
											{...register('end_date', {
												required: t('modal.end_date_required'),
												validate: {
													futureDate: (value: string) => {
														if (isFutureDate(value)) return true;
														return t('modal.end_date_future');
													},
													afterStart: (value: string, formValues: FormValues) => {
														if (endDateIsAfterStartDate(value, formValues)) {
															return true;
														}
														return t('modal.end_date_after_start');
													},
												},
											})}
										/>
										<FormErrorMessage>{errors?.end_date?.message}</FormErrorMessage>
										<FormHelperText>{t('modal.end_date_helper')}</FormHelperText>
									</FormControl>

									<SelectIncentivesDropdown register={register} recipients={recipientData.recipients} value={claimableAwardId} />

									<ImageUploadField image={announcement?.image} setRemoveImage={setRemoveImage} />

									<Box>
										<FormLabel fontSize={50} isOptional>
											{t('modal.notification_label')}
										</FormLabel>
										<FormControl>
											<CheckboxGroup>
												<Checkbox isDisabled={isNotificationCheckboxDisabled()} {...register('email_notification')}>
													<Text display="inline-block" pt={1.5} fontSize={50} fontWeight="normal" lineHeight={5}>
														{t('modal.email_notification_label')}
													</Text>
												</Checkbox>
												<Checkbox
													mt={2}
													display={slackMessageEnabled ? 'block' : 'none'}
													isDisabled={isNotificationCheckboxDisabled()}
													{...register('send_slack_message')}
												>
													<Text display="inline-block" fontSize={50} fontWeight="normal" lineHeight={5}>
														{t('modal.send_slack_message_label')}
													</Text>
												</Checkbox>
												<Checkbox
													mt={2}
													display={msteamsMessageEnabled ? 'block' : 'none'}
													isDisabled={isNotificationCheckboxDisabled()}
													{...register('send_ms_teams_message')}
												>
													<Text display="inline-block" fontSize={50} fontWeight="normal" lineHeight={5}>
														{t('modal.send_ms_teams_message_label')}
													</Text>
												</Checkbox>
											</CheckboxGroup>
										</FormControl>
									</Box>
									<Spacer h={5} />
								</Stack>
							</ModalBody>
						)}

						<ModalFooter
							flexDirection="column"
							{...(!isHomepageRedesignEnabled && {
								borderTopColor: 'border.default',
								borderTopStyle: 'solid',
								borderTopWidth: '1px',
							})}
							{...(isHomepageRedesignEnabled && { px: 4, py: 4 })}
						>
							{isHomepageRedesignEnabled ? (
								<Stack spacing={3} w="100%">
									<Button colorScheme="primary" data-testid="save-button" isDisabled={isSaveDisabled()} isLoading={isLoading} type="submit" w="100%">
										{getButtonText()}
									</Button>
									<Button colorScheme="secondary" data-testid="delete-button" isLoading={isDeleting} onClick={handleDelete} w="100%">
										{t('modal.delete_button')}
									</Button>
								</Stack>
							) : (
								<Flex justify="space-between" w="100%">
									<Button colorScheme="secondary" data-testid="delete-button" isLoading={isDeleting} onClick={handleDelete}>
										{t('modal.delete_button')}
									</Button>
									<Button colorScheme="primary" data-testid="save-button" isDisabled={isSaveDisabled()} isLoading={isLoading} type="submit">
										{getButtonText()}
									</Button>
								</Flex>
							)}
							{errorMessage && (
								<Text color="error.text" fontSize={50} mt={4} textAlign="right" w="100%">
									{errorMessage}
								</Text>
							)}
						</ModalFooter>
					</ModalContent>
				</FormProvider>
			</Modal>
		</>
	);
};

EditAnnouncementModal.displayName = 'Edit Announcement Modal';

export { EditAnnouncementModal };
